import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Video, Download, RefreshCw, CheckCircle, Search, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VideoHistory from './VideoHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProgressBar = ({ progress, label }) => (
  <div className="w-full space-y-2">
    <div className="flex justify-between text-xs text-gray-500">
      <span>{label}</span>
      <span>{progress}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const AvatarVideoPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [script, setScript] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [genderFilter, setGenderFilter] = useState('all');
  const [errorMsg, setErrorMsg] = useState('');
  const [recordId, setRecordId] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoadingAvatars(true);
    try {
      const [avRes, vRes] = await Promise.all([
        axios.get(`${API}/heygen/avatars`),
        axios.get(`${API}/heygen/voices`)
      ]);
      setAvatars(avRes.data.avatars || []);
      const vs = vRes.data.voices || [];
      setVoices(vs);
      if (vs.length > 0) setSelectedVoice(vs[0].voice_id);
    } catch { toast.error('Failed to load avatars'); }
    setLoadingAvatars(false);
  };

  const filtered = avatars.filter(a => {
    const matchName = a.avatar_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchGender = genderFilter === 'all' || a.gender === genderFilter;
    return matchName && matchGender;
  });

  const startProgress = () => {
    setProgress(10);
    const steps = [
      { at: 5000,  val: 20 }, { at: 15000, val: 35 }, { at: 30000, val: 50 },
      { at: 60000, val: 65 }, { at: 90000, val: 78 }, { at: 120000, val: 88 },
      { at: 150000, val: 93 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(v => Math.max(v, s.val)), s.at));
  };

  const generate = async () => {
    if (!selectedAvatar) { toast.error('Select an avatar'); return; }
    if (!script.trim()) { toast.error('Write your script'); return; }
    const token = getToken();
    if (!token) { toast.error('Please sign in'); return; }
    setStep(3); setStatusMsg('Submitting your request...'); setProgress(5); setErrorMsg('');
    try {
      const res = await axios.post(`${API}/heygen/avatar-video`, {
        avatar_id: selectedAvatar.avatar_id,
        voice_id: selectedVoice,
        script,
        title: `Avatar - ${selectedAvatar.avatar_name}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success && res.data.video_id) {
        setRecordId(res.data.record_id);
        setStatusMsg('Video queued — AI is generating it...');
        startProgress();
        pollVideo(res.data.video_id, res.data.record_id);
      }
    } catch (e) {
      setStep(2);
      setErrorMsg(e.response?.data?.detail || 'Generation failed. Try again.');
    }
  };

  const pollVideo = (id, recId) => {
    let n = 0;
    const t = setInterval(async () => {
      n++;
      if (n > 120) { clearInterval(t); setStep(2); setErrorMsg('Timed out. Try again.'); return; }
      try {
        const res = await axios.get(`${API}/heygen/video-status/${id}`);
        const { status, video_url, thumbnail_url } = res.data;
        if (status === 'completed' && video_url) {
          clearInterval(t); setProgress(100); setVideoUrl(video_url); setStep(4);
          toast.success('Avatar video ready!');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'completed', video_url, thumbnail_url },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else if (status === 'failed') {
          clearInterval(t); setStep(2); setErrorMsg('Generation failed. Try again.');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'failed' },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else {
          setStatusMsg(`Processing your video... (${status})`);
          setProgress(v => Math.max(v, Math.min(93, 10 + n * 1.5)));
        }
      } catch (_) {}
    }, 5000);
  };

  const reset = () => { setStep(1); setSelectedAvatar(null); setScript(''); setVideoUrl(null); setProgress(0); setErrorMsg(''); setRecordId(null); };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = videoUrl; a.download = 'avatar-video.mp4'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/creator-ai/heygen-hub')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-1.5 text-xs sm:text-sm">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">Avatar Video</h1>
                <p className="text-xs text-gray-500">Pick avatar + write script · 200 credits</p>
              </div>
            </div>
            {step < 3 && (
              <div className="flex items-center gap-1">
                {['Avatar','Script','Done'].map((label, idx) => (
                  <React.Fragment key={idx}>
                    <div className="flex items-center gap-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === idx+1 ? 'bg-blue-700 text-white' : idx+1 < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>{idx+1 < step ? '✓' : idx+1}</div>
                      <span className="hidden sm:inline text-xs text-gray-500">{label}</span>
                    </div>
                    {idx < 2 && <div className={`w-3 sm:w-5 h-0.5 ${idx+1 < step ? 'bg-green-400':'bg-gray-200'}`}/>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">

        {/* STEP 1 — Pick Avatar */}
        {step === 1 && (
          <div className="space-y-3">
            <VideoHistory videoType="avatar_video" accentColor="blue" />
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Choose Your Avatar</h2>
              <p className="text-gray-500 text-xs">Pick from 1,000+ AI Avatars</p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name..." className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div className="flex gap-2">
              {[{v:'all',l:'All'},{v:'male',l:'♂ Male'},{v:'female',l:'♀ Female'}].map(g => (
                <button key={g.v} onClick={() => setGenderFilter(g.v)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-all ${
                    genderFilter === g.v ? 'bg-blue-700 text-white border-blue-600':'border-gray-300 text-gray-600 bg-white'
                  }`}>{g.l}</button>
              ))}
            </div>
            {loadingAvatars ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[...Array(6)].map((_,i) => <div key={i} className="bg-gray-100 rounded-2xl h-56 animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pb-2">
                {filtered.slice(0,60).map(av => (
                  <div key={av.avatar_id} onClick={() => { setSelectedAvatar(av); setStep(2); }}
                    className={`rounded-2xl overflow-hidden border-2 cursor-pointer transition-all active:scale-95 ${
                      selectedAvatar?.avatar_id === av.avatar_id ? 'border-blue-500':'border-gray-200 hover:border-blue-300'
                    }`}>
                    {av.preview_image_url
                      ? <img src={av.preview_image_url} alt={av.avatar_name} className="w-full h-44 sm:h-52 object-cover object-center bg-gray-100" loading="lazy" decoding="async"/>
                      : <div className="w-full h-44 sm:h-52 bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center"><Users className="h-10 w-10 text-blue-400" /></div>
                    }
                    <div className="p-2 bg-white">
                      <p className="text-xs font-semibold text-gray-800 truncate text-center">{av.avatar_name}</p>
                      <p className="text-xs text-gray-400 text-center capitalize">{av.gender}</p>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <p className="col-span-2 sm:col-span-3 text-center py-10 text-gray-400 text-sm">No avatars found</p>}
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Script */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Write Your Script</h2>
              <p className="text-gray-500 text-xs">Your avatar will speak this text</p>
            </div>
            {selectedAvatar && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-3">
                {selectedAvatar.preview_image_url &&
                  <img src={selectedAvatar.preview_image_url} alt={selectedAvatar.avatar_name}
                    className="w-14 h-14 rounded-xl object-cover object-center border-2 border-blue-300 flex-shrink-0" loading="lazy" decoding="async"/>}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{selectedAvatar.avatar_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{selectedAvatar.gender}</p>
                </div>
                <button onClick={() => setStep(1)} className="text-xs text-blue-600 underline">Change</button>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">🎙️ Select Voice</label>
                <select value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
                  {voices.map(v => (
                    <option key={v.voice_id} value={v.voice_id}>
                      {v.display_name} {v.locale ? `(${v.locale})`:''} {v.gender ? `· ${v.gender}`:''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Your Script</label>
                <textarea value={script} onChange={e => setScript(e.target.value)} rows={5}
                  placeholder="Write what your avatar should say...\n\nExample: Hello! Welcome to Nerd AI — your ultimate learning companion."
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
                <p className="text-xs text-gray-400 mt-1">{script.length} chars — recommended 50–500</p>
              </div>
              {errorMsg && <p className="text-xs text-red-500 bg-red-50 rounded-xl px-4 py-3">{errorMsg}</p>}
              <div className="flex flex-col gap-3">
                <button onClick={generate} disabled={!script.trim()}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                  <Video className="h-4 w-4" /> Generate Avatar Video (200 credits)
                </button>
                <button onClick={() => setStep(1)} className="w-full border-2 border-gray-300 text-gray-700 font-semibold py-3 rounded-xl text-sm">← Back to Avatars</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Generating */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                <Video className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Generating Your Avatar Video</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{statusMsg}</p>
              </div>
            </div>
            <ProgressBar progress={progress} label="Generating..." />
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-800">⏱ Average time: <strong>1–3 minutes</strong>. Please keep this page open.</p>
            </div>
          </div>
        )}

        {/* STEP 4 — Done */}
        {step === 4 && videoUrl && (
          <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold text-base">Avatar Video Ready!</span>
            </div>
            <div className="bg-black rounded-xl overflow-hidden">
              <video
                src={videoUrl} controls muted playsInline
                className="w-full max-h-[300px] sm:max-h-[420px]"
                onError={() => toast.error('Preview failed — use the Open button to watch your video')}
              />
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={handleDownload}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm">
                <Download className="h-4 w-4" /> Download Video
              </button>
              <a href={videoUrl} target="_blank" rel="noreferrer"
                className="w-full border-2 border-blue-300 text-blue-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-50 text-sm">
                <ExternalLink className="h-4 w-4" /> Open in Browser
              </a>
              <button onClick={reset}
                className="w-full border-2 border-gray-200 text-gray-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                <RefreshCw className="h-4 w-4" /> Create Another
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AvatarVideoPage;
